import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Eyebrow, Reveal, SectionTitle } from "./Reveal";
import { ROOM_TIERS, WHATSAPP_DISPLAY, WHATSAPP_NUMBER } from "./data";

const fieldClass =
  "w-full border border-ivory/15 bg-forest/60 px-4 py-3.5 text-[15px] text-ivory placeholder:text-ivory/35 focus:border-saffron focus:outline-none transition-colors duration-300";
const labelClass = "mb-2 block text-[11px] font-medium uppercase tracking-[0.22em] text-ivory/55";

export default function EnquiryForm() {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    travellers: "",
    month: "",
    tier: ROOM_TIERS[0].name,
    message: "",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) {
      toast.error("Please share your name and a contact detail so we can write back.");
      return;
    }
    const lines = [
      "New Enquiry — South India Wildlife & Slow Travel Journey",
      "",
      `Name: ${form.name}`,
      `Contact: ${form.contact}`,
      `Travellers: ${form.travellers || "Not specified"}`,
      `Preferred month: ${form.month || "Flexible"}`,
      `Stay preference: ${form.tier}`,
      form.message ? `Note: ${form.message}` : "",
    ].filter(Boolean);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — your enquiry is ready to send.");
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="bg-forest">
      <div className="mx-auto max-w-editorial px-6 py-24 md:px-10 md:py-32">
        <Reveal>
          <Eyebrow center>Tailor-made for you</Eyebrow>
        </Reveal>
        <Reveal delay={0.12}>
          <SectionTitle before="Begin Your" accent="Journey" dark center className="mt-7" />
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mx-auto mt-6 max-w-md text-center text-sm leading-[1.85] text-ivory/60">
            Every departure is tailored to your dates, group and pace. Private departures on request, small groups
            only.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <form
            data-testid="enquiry-form"
            onSubmit={handleSubmit}
            className="mx-auto mt-14 max-w-3xl border border-ivory/12 bg-ivory/[0.03] p-8 md:p-12"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="enquiry-name" className={labelClass}>
                  Full name
                </label>
                <input
                  id="enquiry-name"
                  data-testid="enquiry-name-input"
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Ananya Rao"
                  className={fieldClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="enquiry-contact" className={labelClass}>
                  Email or phone
                </label>
                <input
                  id="enquiry-contact"
                  data-testid="enquiry-contact-input"
                  type="text"
                  value={form.contact}
                  onChange={set("contact")}
                  placeholder="you@example.com"
                  className={fieldClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="enquiry-travellers" className={labelClass}>
                  Travellers
                </label>
                <input
                  id="enquiry-travellers"
                  data-testid="enquiry-travellers-input"
                  type="number"
                  min="1"
                  max="12"
                  value={form.travellers}
                  onChange={set("travellers")}
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="enquiry-month" className={labelClass}>
                  Preferred month
                </label>
                <input
                  id="enquiry-month"
                  data-testid="enquiry-month-input"
                  type="text"
                  value={form.month}
                  onChange={set("month")}
                  placeholder="February"
                  className={fieldClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="enquiry-tier" className={labelClass}>
                  Stay / room preference
                </label>
                <select
                  id="enquiry-tier"
                  data-testid="enquiry-tier-select"
                  value={form.tier}
                  onChange={set("tier")}
                  className={`${fieldClass} cursor-pointer appearance-none`}
                >
                  {ROOM_TIERS.map((t) => (
                    <option key={t.name} value={t.name} className="bg-forest text-ivory">
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="enquiry-message" className={labelClass}>
                  Message / special requests
                </label>
                <textarea
                  id="enquiry-message"
                  data-testid="enquiry-message-input"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Cameras you carry, occasions you're celebrating, pace you prefer…"
                  className={`${fieldClass} resize-none`}
                />
              </div>
            </div>

            <div className="mt-10 text-center">
              <button
                type="submit"
                data-testid="enquiry-submit-button"
                className="inline-flex items-center gap-3 bg-saffron px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-forest transition-colors duration-300 hover:bg-ivory"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
                Send enquiry via WhatsApp
              </button>
              <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-ivory/45">
                We typically respond within a few hours · {WHATSAPP_DISPLAY}
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
